import { Grid } from '@mantine/core';
import React, { useMemo } from 'react';
import { uuid } from '../../../CodesThatsGottaGoSomewhereElse';
import { JiraFieldData2 } from '../../../api/endpoints/jira';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { JiraFieldInput } from './inputs/JiraFieldInput';

const useFieldLayout = (createSchema: JiraFieldData2[]) => {
    // Let's try and influence the layout to look okayisk.  We basically want
    // Assignee | Reporter
    // Summary
    // Description
    // <Dropdowns>
    // <Numbers/Strings>
    // <Checkboxes>
    return useMemo(() => {
        if (!createSchema?.length) return [];
        const assignee = createSchema.find(x => x.id === 'assignee');
        const reporter = createSchema.find(x => x.id === 'reporter');
        const summary = createSchema.find(x => x.id === 'summary');
        const description = createSchema.find(x => x.id === 'description');

        const fixed = [assignee, reporter, summary, description];
        const flex = createSchema.filter(x => !fixed.includes(x));

        const users = flex.filter(x => x.type === 'user');
        const userMulti = users.filter(x => x.type === 'multi-user');
        const options = flex.filter(x => x.type === 'option' || x.type === 'option-with-child');
        const strNums = flex.filter(x => x.type === 'string' || x.type === 'number');
        const checkboxes = flex.filter(x => x.type === 'toggles');

        const skip = [...users, ...options, ...strNums, ...checkboxes];
        const rest = flex.filter(x => !skip.includes(x));

        const row = (fields: JiraFieldData2[]) => ({
            id: uuid(),
            fields,
        });

        const toTwoCol = (fields: JiraFieldData2[]) =>
            Array.from({ length: Math.ceil(fields.length / 2) })
                .map((_, i) => row(fields.slice(i * 2, (i + 1) * 2)));

        return [
            row([summary]),
            row([description]),
            row([assignee, reporter]),
            ...toTwoCol(users),
            ...userMulti.map(x => row([x])),
            ...toTwoCol(options),
            ...toTwoCol(strNums),
            ...toTwoCol(checkboxes),
            ...rest.map(x => row([x])),
        ];
    }, [createSchema]);
};

export type CreateJiraIssueFormProps = {
    fields: JiraFieldData2[],
    values: Record<string, any>,
    errors?: string[],
    onChange(id: string, value: any): void,
};

export const CreateJiraIssueForm = ({ fields, values, errors, onChange }: CreateJiraIssueFormProps) => {

    const layout = useFieldLayout(fields);
    const hasError = new Set(errors?.map(x => x.toLowerCase()) ?? []);

    return <Flexer grow flex="column" gap="1">
        {layout?.map(row =>
            <Grid key={row.id}>
                {row.fields.map(field =>
                    <Grid.Col key={field.id} span={12 / row.fields.length}>
                        <JiraFieldInput
                            field={field}
                            value={values[field.id]}
                            error={hasError.has(field.id?.toLowerCase())}
                            onChange={(val) => onChange(field.id, val)}
                        />
                    </Grid.Col>
                )}
            </Grid>
        )}
    </Flexer>;
};
